import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { queryAnalysisSegmentComparisons, searchAnalysisSegmentMappings } from '../../utils/explorerApi';
import { downloadCompareCsv, downloadComparePdf } from '../../utils/compareExport';
import { buildCompareSeries } from './compareSeries';
import { categoryColorMap, UNPALETTED_CATEGORY_COLOR } from './categoryColors';
import CompareChart from './CompareChart';
import CompareSegmentPicker from './CompareSegmentPicker';
import CompareSummaryBox from './CompareSummaryBox';

const PAGE_SIZE = 25;
// The A/B report is sized to the window rather than to a guess. The gutter is
// the breathing room under the last row; the floor keeps the report usable if
// it is ever mounted low on a very short viewport.
// Sequential ramp for ORDERED answers (a rating scale). One hue, light to dark,
// so the ramp itself carries "more" — a categorical set of hues on ordered steps
// would claim the steps are unrelated identities. Steps are the design system's
// blue ramp; the darkest is the single-series bar colour.
// A question is one band: its answers and its statistics share a tint, and
// consecutive questions alternate, so a long report reads as questions rather
// than as a wall of rows. The question column carries a standing tint of its
// own — it is the spine the reader scans down.
const BAND_BG = '#F8F9FA';
const QUESTION_COLUMN_BG = '#F1F3F4';
const QUESTION_COLUMN_BAND_BG = '#E8EAED';
const ORDERED_RAMP = ['#D2E3FC', '#A8C7FA', '#7BAAF7', '#4A90E2', '#1967D2'];
const SINGLE_BAR_COLOR = '#1967D2';
const orderedFill = (index, total) => ORDERED_RAMP[total <= 1 ? ORDERED_RAMP.length - 1 : Math.round((index / (total - 1)) * (ORDERED_RAMP.length - 1))];
const MIN_REPORT_HEIGHT = 320;
const REPORT_BOTTOM_GUTTER = 24;
// Plain names for the wire types, matching the tag Describe shows.
const QUESTION_TYPE_LABEL = {
  text: 'Text', number: 'Scalar', scale: 'Set member', choice: 'Set member', yes_no: 'Boolean',
};
// Cloned from the Describe toolbar so both subtabs present one control family.
const compareTopButtonStyle = { fontSize: 12, padding: '4px 10px', borderRadius: 4, border: '1px solid #DADCE0', background: '#FFFFFF', color: '#5F6368', cursor: 'pointer' };
const compareExportMenuItemStyle = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, color: '#202124', background: 'none', border: 'none', cursor: 'pointer' };
const refKey = (mapping) => `${mapping?.deployment_id || ''}/${mapping?.physical_segment_id || ''}`;
const mappingRef = (candidate) => candidate.mapping || candidate;
// What a mapping MEANS (Section 4.1). Anything that is not explicitly a whole
// roster is a subset, which is what every mapping made before mapping_scope
// existed actually is.
const WHOLE_ROSTER = 'whole_roster';
const SUBSET = 'subset';
const scopeOf = (candidate) => (candidate?.mapping_scope === WHOLE_ROSTER ? WHOLE_ROSTER : SUBSET);
const candidateKey = (candidate) => refKey(mappingRef(candidate));

// The deployment resolves to exactly one survey version, so the label states
// that version by its authored number.  Raw version UUIDs are request data and
// are never rendered; a version with no returned number degrades to the plain
// noun rather than exposing an identifier.
function versionLabel(candidate) {
  if (candidate.pinned_unavailable) return 'Survey version unavailable';
  return typeof candidate.survey_version_number === 'number'
    ? `Survey version ${candidate.survey_version_number}`
    : 'Survey version';
}

// Segment name, survey name, version, population. The deployment label is the
// deploy slug — an identifier, not a name — and authors do not read slugs. The
// Segment name already says which population this column is, so the slug added
// a line of machine text to every column header and every cell for nothing.
function MappingLabel({ candidate }) {
  return <span>
    <strong>{candidate.logical_segment_name || 'Unnamed Segment'}</strong>
    <span> · {candidate.survey_label || 'Survey unavailable'}</span>
    <span> · {versionLabel(candidate)}</span>
    <span> · population {candidate.included_recipient_count ?? 0}</span>
  </span>;
}

const resultMappingKey = (mapping) => refKey(mapping?.mapping || mapping);
const asObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (_) { return {}; }
};
const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Not returned';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};
const numberLabel = (value) => (typeof value === 'number' ? value.toLocaleString() : displayValue(value));
const percentLabel = (value) => (typeof value === 'number' ? `${value.toLocaleString()}%` : displayValue(value));

function Fact({ label, children }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}><span style={{ color: '#5F6368' }}>{label}</span><span>{children}</span></div>;
}

function Qualifications({ qualifications = [] }) {
  const returned = qualifications.filter(Boolean);
  if (!returned.length) return null;
  return <ul aria-label="Returned qualifications" style={{ margin: '8px 0 0', paddingLeft: 18 }}>{returned.map((qualification, index) => <li key={`${qualification.code || qualification.message || 'qualification'}-${index}`}>{qualification.message || qualification.reason || qualification.code || 'Qualification returned by the analysis service.'}</li>)}</ul>;
}

function RawAnswers({ payload }) {
  const observations = asObject(payload).selected_observations;
  const [expanded, setExpanded] = useState(false);
  if (!Array.isArray(observations)) return null;
  const toggleFromKeyboard = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setExpanded(value => !value);
    }
  };
  return <details open={expanded} onToggle={(event) => setExpanded(event.currentTarget.open)} style={{ marginTop: 10 }}>
    <summary aria-label={`Show raw recorded answers (${observations.length})`} aria-expanded={expanded} onKeyDown={toggleFromKeyboard}>Raw recorded answers ({observations.length})</summary>
    <div tabIndex="0" aria-label="Raw recorded answer provenance" style={{ overflowX: 'auto', marginTop: 6 }}>
      <table><thead><tr><th>Recorded answer</th><th>Display answer</th><th>Source</th><th>Quoted-string provenance</th><th>Completion time</th></tr></thead>
        <tbody>{observations.map((observation, index) => <tr key={`${observation.response_id || observation.session_id || 'answer'}-${index}`}>
          <td>{observation.raw_answer === '' ? 'Empty string' : displayValue(observation.raw_answer)}</td>
          <td>{observation.display_answer === '' ? 'Empty string' : displayValue(observation.display_answer)}</td>
          <td>{displayValue(observation.answer_source)}</td>
          <td>{observation.quoted_string_provenance ? 'Quoted string recorded' : 'Not marked'}</td>
          <td>{observation.completion_time_missing ? 'Missing' : 'Recorded'}</td>
        </tr>)}</tbody>
      </table>
    </div>
  </details>;
}

// `colors` is the shared category colour map; without one the bars are a single
// fill, which is right when the bars are one ordered scale rather than separate
// identities.
function DistributionBars({ counts = {}, percentages = {}, label, colors = null, ordered = false }) {
  const entries = Object.keys(counts || {});
  if (!entries.length) return null;
  return <div role="img" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '8px 0' }}>{entries.map((key, index) => {
    const percentage = percentages?.[key];
    // An ORDERED answer takes a sequential ramp: the steps are one measure, and
    // light-to-dark says "more" the way separate hues never could. Separate
    // identities take the validated categorical palette.
    const fill = ordered ? orderedFill(index, entries.length) : (colors ? (colors.get(key) || UNPALETTED_CATEGORY_COLOR) : SINGLE_BAR_COLOR);
    return <div key={key} style={{ display: 'grid', gridTemplateColumns: 'minmax(72px, 1fr) minmax(140px, 3fr)', gap: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 12 }}>{key || 'Empty string'}</span>
      {/* The count rides in the bar, right-justified, rather than repeating
          underneath it: the same fact printed twice is the reader's work, not
          the report's. Ink stays a text token so the number never poses as a
          second encoding. */}
      <span style={{ position: 'relative', display: 'block', background: '#E8EAED', borderRadius: 3, minHeight: 18 }}>
        <span data-category-bar={key} style={{ display: 'block', width: typeof percentage === 'number' ? `${percentage}%` : '0%', background: fill, borderRadius: 3, minHeight: 18 }} />
        <span data-bar-value={key} style={{ position: 'absolute', right: 6, top: 0, lineHeight: '18px', fontSize: 11, color: '#202124', whiteSpace: 'nowrap' }}>
          {numberLabel(counts[key])}{typeof percentage === 'number' ? ` (${percentLabel(percentage)})` : ''}
        </span>
      </span>
    </div>;
  })}</div>;
}

function NumericRangeSummary({ descriptive }) {
  const returned = { min: descriptive.min, q1: descriptive.q1, median: descriptive.median, q3: descriptive.q3, max: descriptive.max };
  const values = Object.values(returned);
  const complete = values.every(value => typeof value === 'number' && Number.isFinite(value));
  if (!complete || returned.min > returned.q1 || returned.q1 > returned.median || returned.median > returned.q3 || returned.q3 > returned.max) return <div role="status" aria-label="Numeric range plot unavailable">Returned numeric range and quartile values are incomplete; numeric facts remain below.</div>;
  const range = returned.max - returned.min;
  const position = (value) => (range === 0 ? 50 : ((value - returned.min) / range) * 100);
  const q1 = position(returned.q1);
  const q3 = position(returned.q3);
  const median = position(returned.median);
  const label = `Numeric box plot from returned summary: lowest ${numberLabel(returned.min)}, first quartile ${numberLabel(returned.q1)}, median ${numberLabel(returned.median)}, third quartile ${numberLabel(returned.q3)}, highest ${numberLabel(returned.max)}.`;
  return <div role="img" aria-label={label} data-proportional-box-plot="true" data-equal-range={range === 0 ? 'true' : undefined} style={{ position: 'relative', height: 46, margin: '8px 0 18px' }}>
    <span aria-hidden="true" style={{ position: 'absolute', top: 22, left: 0, width: '100%', height: 2, background: '#5F6368' }} />
    <span aria-hidden="true" data-box-range style={{ position: 'absolute', top: 11, left: `${q1}%`, width: `${q3 - q1}%`, minWidth: range === 0 ? 2 : undefined, height: 22, border: '2px solid #1967D2', background: '#E8F0FE', transform: 'translateX(0)' }} />
    <span aria-hidden="true" data-median-marker style={{ position: 'absolute', top: 8, left: `${median}%`, height: 28, borderLeft: '2px solid #202124' }} />
    {Object.entries(returned).map(([key, value]) => <span key={key} aria-hidden="true" data-plot-marker={key} style={{ position: 'absolute', top: 38, left: `${position(value)}%`, transform: 'translateX(-50%)', fontSize: 10, whiteSpace: 'nowrap' }}>{numberLabel(value)}</span>)}
  </div>;
}

function CountList({ counts = {}, percentages = {}, ordered = false, categoryColors = null }) {
  const keys = Object.keys(counts || {});
  if (!keys.length) return <div style={{ color: '#5F6368' }}>No answers were returned for this Segment.</div>;
  const label = ordered ? 'Ordered answer distribution chart' : 'Answer distribution chart';
  return <div aria-label={ordered ? 'Ordered answer distribution' : 'Answer distribution'}><DistributionBars counts={counts} percentages={percentages} label={label} colors={ordered ? null : categoryColors} ordered={ordered} /></div>;
}

// Counts of answers that are character-for-character identical. Trimmed only,
// never case-folded or stemmed: two answers are the same answer or they are two
// answers, and deciding otherwise would be inventing a theme. Returns null when
// there is nothing to count, and withholds the chart when the answers are all
// but distinct — a bar per person is a list wearing a chart's clothes.
const IDENTICAL_ANSWER_CHART_LIMIT = 12;
function identicalAnswerCounts(payload) {
  const observations = asObject(payload).selected_observations;
  if (!Array.isArray(observations)) return null;
  const counts = {};
  let answered = 0;
  observations.forEach((observation) => {
    const value = String(observation?.display_answer ?? '').trim();
    if (!value) return;
    answered += 1;
    counts[value] = (counts[value] || 0) + 1;
  });
  const keys = Object.keys(counts);
  if (!answered || !keys.length) return null;
  const ordered = keys.sort((left, right) => counts[right] - counts[left] || left.localeCompare(right));
  if (ordered.length > IDENTICAL_ANSWER_CHART_LIMIT) return { distinct: ordered.length, answered, counts: {}, percentages: {} };
  return {
    distinct: ordered.length,
    answered,
    counts: Object.fromEntries(ordered.map(key => [key, counts[key]])),
    percentages: Object.fromEntries(ordered.map(key => [key, (counts[key] * 100) / answered])),
  };
}

function OwnResult({ canonicalType, cell, categoryColors = null }) {
  const detail = asObject(cell.detail);
  const type = canonicalType || detail.question_type;
  const descriptive = asObject(detail.descriptive);
  const unsupported = type === 'email' || type === 'file' || !['number', 'scale', 'yes_no', 'choice', 'text'].includes(type);
  if (unsupported) return <div aria-label="Unsupported question type"><strong>Unsupported question type</strong><div>{detail.limitation || 'V1 response comparison is unsupported for this response type.'}</div></div>;
  if (type === 'number') {
    const fields = [['Mean', descriptive.mean], ['Median', descriptive.median], ['Standard deviation', descriptive.stddev], ['Lowest returned value', descriptive.min], ['First quartile', descriptive.q1], ['Third quartile', descriptive.q3], ['Highest returned value', descriptive.max], ['Range', descriptive.range], ['Interquartile range', descriptive.iqr]];
    return <div aria-label="Numeric result"><strong>Numeric description</strong><NumericRangeSummary descriptive={descriptive} /><div aria-label="Numeric distribution comparison">{fields.filter(([, value]) => value !== undefined && value !== null).map(([label, value]) => <Fact key={label} label={label}>{numberLabel(value)}</Fact>)}</div>
      {cell.calculation_n === 1 && <div>Sample variance withheld: calculation n must be at least 2.</div>}
      {cell.calculation_n >= 2 && descriptive.variance !== undefined && <Fact label="Sample variance">{numberLabel(descriptive.variance)}</Fact>}
    </div>;
  }
  if (type === 'scale') return <div aria-label="Ordinal result"><strong>Ordered response distribution</strong><Fact label="Median">{numberLabel(descriptive.median)}</Fact>{descriptive.iqr !== undefined && <Fact label="Interquartile range">{numberLabel(descriptive.iqr)}</Fact>}<CountList ordered counts={detail.ordered_category_counts} percentages={detail.ordered_category_percentages} /></div>;
  if (type === 'yes_no' || type === 'choice') return <div aria-label="Categorical result"><strong>{type === 'yes_no' ? 'Yes/no' : 'Choice'} distribution</strong><div style={{ color: '#5F6368' }}>Percentages use returned calculation n.</div><CountList counts={detail.category_counts} percentages={detail.category_percentages} categoryColors={categoryColors} /></div>;
  const derived = asObject(detail.derived_categories);
  if (!Object.keys(derived).length) {
    // No classifier has run, so there are no themes — but identical answers are
    // a fact, not an interpretation, and counting them is collation rather than
    // invention. Twenty answers that are two sentences repeated ten times each
    // is the finding; a list of twenty rows hides it.
    const identical = identicalAnswerCounts(cell.payload);
    if (identical) return <div aria-label="Text answer distribution"><strong>Answers given</strong><div style={{ color: '#5F6368', fontSize: 12 }}>{identical.distinct} distinct {identical.distinct === 1 ? 'answer' : 'answers'} across {identical.answered} answered. Identical wording only; no themes are inferred.</div><DistributionBars counts={identical.counts} percentages={identical.percentages} label="Identical answer distribution" colors={categoryColors} /></div>;
    return <div aria-label="Unclassified text result"><strong>Raw text responses</strong><div>{identical === null ? 'No answers were returned for this Segment.' : ''}</div></div>;
  }
  if (Object.keys(derived).length) return <div aria-label="Classified text result"><strong>Derived text categories</strong><CountList counts={derived.category_counts} percentages={derived.category_percentages} categoryColors={categoryColors} /><Fact label="Classification provenance">{displayValue(derived.classification_provenance)}</Fact><Fact label="Category provenance">{displayValue(derived.category_provenance)}</Fact><Fact label="Mapped / unmapped / stale">{`${numberLabel(derived.mapped_count)} / ${numberLabel(derived.unmapped_count)} / ${numberLabel(derived.stale_index_count)}`}</Fact></div>;
  return <div aria-label="Unclassified text result"><strong>Raw text responses</strong><div>Responses remain available side by side below; V1 does not invent themes or numeric summaries.</div></div>;
}

// showRawAnswers is off in the A/B report. Twenty rows of recorded/display/
// source/provenance per Segment per question is a per-answer audit trail, and
// it buries the thing the report is for: how the two distributions differ.
// The counts and bars above answer that; the intra-deployment surface keeps the
// trail, where one question is under a microscope rather than a whole survey.
function MappingResultCard({ mapping, cell, canonicalType, categoryColors = null, showRawAnswers = true }) {
  if (!cell) return <article aria-label={`${mapping.logical_segment_name || 'Segment'} result`} style={{ border: '1px solid #DADCE0', borderRadius: 6, padding: 12 }}><MappingLabel candidate={mapping} /><div>No result cell was returned for this mapping.</div></article>;
  const missingRate = cell.missing_rate_not_applicable ? 'Not applicable' : cell.missing_rate === undefined || cell.missing_rate === null ? 'Not returned' : percentLabel(cell.missing_rate * 100);
  return <article aria-label={`${mapping.logical_segment_name || 'Segment'} result`} style={{ border: '1px solid #DADCE0', borderRadius: 6, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
    <MappingLabel candidate={mapping} />
    <div aria-label="Factual status"><Fact label="Eligible n">{numberLabel(cell.eligible_n)}</Fact><Fact label="Answered n">{numberLabel(cell.answered_n)}</Fact><Fact label="Missing n">{numberLabel(cell.missing_n)}</Fact><Fact label="Missing rate">{missingRate}</Fact><Fact label="Calculation n">{numberLabel(cell.calculation_n)}</Fact><Fact label="Calculation-ineligible values">{numberLabel(cell.calculation_ineligible_value_count)}</Fact>
      {cell.calculation_ineligible_value_count > 0 && <div>Values excluded from this calculation{cell.reason ? `: ${cell.reason}` : '.'}</div>}
      {cell.selected_completion_time_missing_count > 0 && <Fact label="Selected completion time missing">{numberLabel(cell.selected_completion_time_missing_count)}</Fact>}
      {cell.status && <Fact label="Cell status">{cell.status}</Fact>}{cell.reason && <Fact label="Reason">{cell.reason}</Fact>}
    </div>
    <OwnResult canonicalType={canonicalType} cell={cell} categoryColors={categoryColors} />
    <Qualifications qualifications={cell.qualifications} />
    {showRawAnswers && <RawAnswers payload={cell.payload} />}
  </article>;
}

function DeploymentUnattributedFacts({ counts = [], mappings = [], questionId }) {
  const returned = counts.filter(item => item?.question_id === questionId);
  const byDeployment = new Map();
  returned.forEach((item) => {
    const key = item.deployment_id;
    if (!byDeployment.has(key)) byDeployment.set(key, item);
  });
  if (!byDeployment.size) return null;
  const deploymentLabels = new Map(mappings.map(mapping => [mapping.mapping?.deployment_id || mapping.deployment_id, mapping.deployment_label || 'Deployment unavailable']));
  return <section aria-label="Deployment-level unattributed answered-session facts"><h3>Deployment facts</h3>{[...byDeployment.values()].map(item => <Fact key={`${item.deployment_id}/${item.question_id}`} label={`Unattributed answered sessions — ${deploymentLabels.get(item.deployment_id) || item.deployment_id}`}>{numberLabel(item.unattributed_answered_session_count)}</Fact>)}</section>;
}

function refDescription(ref, mappingByKey) {
  const mapping = mappingByKey.get(resultMappingKey(ref));
  return mapping?.logical_segment_name || `${ref?.deployment_id || 'unknown deployment'} / ${ref?.physical_segment_id || 'unknown mapping'}`;
}

function StatisticalEvidence({ result, mappingByKey }) {
  const pairs = Array.isArray(result.pairwise_comparisons) ? result.pairwise_comparisons : [];
  const topLevel = [...(result.statistics || []), ...(result.effects || []), ...(result.qualifications || [])];
  if (!pairs.length && !topLevel.length) return null;
  const groupInputs = (inputs = []) => inputs.map((input, index) => <li key={`${resultMappingKey(input.mapping)}-${index}`}>{refDescription(input.mapping, mappingByKey)}: answered n {numberLabel(input.answered_n)}, calculation n {numberLabel(input.calculation_n)}, ineligible {numberLabel(input.calculation_ineligible_value_count)}</li>);
  const renderedStatistic = (statistic, index) => <div key={`${statistic.method || statistic.statistic_name || 'statistic'}-${index}`}><strong>{statistic.statistic_name || statistic.method || 'Returned statistical artifact'}</strong>{statistic.method && <Fact label="Method">{statistic.method}</Fact>}{statistic.assumptions?.length > 0 && <Fact label="Assumptions">{statistic.assumptions.join('; ')}</Fact>}{statistic.statistic !== undefined && <Fact label="Statistic">{displayValue(statistic.statistic)}</Fact>}{statistic.p_value !== undefined && <Fact label="p-value">{numberLabel(statistic.p_value)}</Fact>}{statistic.adjusted_p_value !== undefined && <Fact label="Adjusted p-value">{numberLabel(statistic.adjusted_p_value)}</Fact>}{statistic.correction && <Fact label="Correction">{statistic.correction}</Fact>}{statistic.withholding_reason && <Fact label="Withheld prerequisite">{statistic.withholding_reason}</Fact>}{statistic.observational_interpretation && <Fact label="Observational interpretation">{statistic.observational_interpretation}</Fact>}{statistic.group_inputs?.length > 0 && <ul aria-label="Statistical group inputs">{groupInputs(statistic.group_inputs)}</ul>}</div>;
  return <section aria-label="Statistical evidence" style={{ border: '1px solid #DADCE0', borderRadius: 6, padding: 12 }}><h3>Statistical evidence</h3>
    {pairs.map((pair, index) => <article key={`${resultMappingKey(pair.left_mapping)}-${resultMappingKey(pair.right_mapping)}-${index}`} style={{ borderTop: index ? '1px solid #E8EAED' : 'none', paddingTop: index ? 10 : 0, marginTop: index ? 10 : 0 }}><strong>{refDescription(pair.left_mapping, mappingByKey)} compared with {refDescription(pair.right_mapping, mappingByKey)}</strong><Fact label="Comparison status">{pair.status || 'Returned'}</Fact>{pair.reason && <Fact label="Reason">{pair.reason}</Fact>}{pair.difference !== undefined && <Fact label="Returned difference">{displayValue(pair.difference)}</Fact>}{pair.withholding_reason && <Fact label="Withheld prerequisite">{pair.withholding_reason}</Fact>}{(pair.statistics || []).map(renderedStatistic)}{(pair.effects || []).map((effect, effectIndex) => <div key={`${effect.name || 'effect'}-${effectIndex}`}><Fact label={effect.name || 'Effect'}>{displayValue(effect.value)}</Fact>{effect.scope && <Fact label="Effect scope">{effect.scope}</Fact>}{effect.group_inputs?.length > 0 && <ul aria-label="Effect group inputs">{groupInputs(effect.group_inputs)}</ul>}</div>)}<Qualifications qualifications={pair.qualifications} /></article>)}
    {topLevel.map((item, index) => item.message ? <Qualifications key={`qualification-${index}`} qualifications={[item]} /> : item.value !== undefined && item.name ? <Fact key={`effect-${index}`} label={item.name}>{displayValue(item.value)}</Fact> : renderedStatistic(item, index))}
  </section>;
}

// The chart is the answer; everything else is the evidence behind it. Detail
// that used to sit above the fold as a wall of label/value rows is collapsed
// into disclosures beneath the chart, where it qualifies the picture rather
// than competing with it.
function EvidenceSection({ label, children, defaultOpen = false }) {
  return <details open={defaultOpen} style={{ border: '1px solid #E8EAED', borderRadius: 6, padding: '8px 12px' }}>
    <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#202124' }}>{label}</summary>
    <div style={{ marginTop: 8 }}>{children}</div>
  </details>;
}

function IntraDeploymentResult({ result, canonicalQuestion, series, showProportion, showCumulative }) {
  const mappings = Array.isArray(result?.mappings) ? result.mappings : [];
  const cells = Array.isArray(result?.cells) ? result.cells : [];
  const mappingByKey = new Map(mappings.map(mapping => [resultMappingKey(mapping), mapping]));
  // The chart axis is already the union of categories across every selected
  // Segment, in one order. Colouring the detail bars off that same list is what
  // makes a category read the same in every card — and keeps the cards agreeing
  // with the chart above them. An ordered scale is excluded: its bars are steps
  // of one measure, not separate identities.
  const categoryColors = series?.kind === 'categorical' && series.questionType !== 'scale'
    ? categoryColorMap(series.categories)
    : null;
  if (!mappings.length) return <section aria-label="Intra-deployment comparison result"><h3>Selected-question comparison</h3><div role="status">No mapping result details were returned.</div></section>;
  return <section aria-label="Intra-deployment comparison result" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <CompareChart series={series} showProportion={showProportion} showCumulative={showCumulative} />
    <EvidenceSection label="Statistical evidence" defaultOpen>
      <StatisticalEvidence result={result} mappingByKey={mappingByKey} />
      <section aria-label="Pairwise overlap statuses" style={{ marginTop: 10 }}><h4 style={{ margin: '0 0 6px' }}>Pairwise overlap statuses</h4>{(result.overlaps || []).length === 0 ? <div>No overlap statuses were returned.</div> : result.overlaps.map((overlap, index) => <div key={`${resultMappingKey(overlap.left_mapping)}-${resultMappingKey(overlap.right_mapping)}-${index}`}><strong>{refDescription(overlap.left_mapping, mappingByKey)} / {refDescription(overlap.right_mapping, mappingByKey)}</strong>: {overlap.overlap_status || 'Returned'} — known overlap {numberLabel(overlap.known_overlap_count)}, unknown identity {numberLabel(overlap.identity_unknown_count)}</div>)}</section>
      <DeploymentUnattributedFacts counts={result.unattributed_answered_session_counts} mappings={mappings} questionId={canonicalQuestion?.question_id} />
    </EvidenceSection>
    <EvidenceSection label="Per-Segment detail and provenance">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>{mappings.map(mapping => <MappingResultCard key={resultMappingKey(mapping)} mapping={mapping} cell={cells.find(cell => resultMappingKey(cell.mapping) === resultMappingKey(mapping) && cell.question_id === canonicalQuestion?.question_id)} canonicalType={canonicalQuestion?.question_type} categoryColors={categoryColors} />)}</div>
    </EvidenceSection>
  </section>;
}

const wholeRowKey = (row, index = 0) => row?.cursor || `${row?.variable_name || 'row'}-${index}`;
const wholeRowID = (row, index = 0) => `analysis-whole-row-${encodeURIComponent(wholeRowKey(row, index))}`;
const sameVariable = (left, right) => String(left || '').toLocaleLowerCase() === String(right || '').toLocaleLowerCase();
const appendServerOrdered = (previous = [], page = [], key) => {
  const seen = new Set(previous.map(key));
  return [...previous, ...page.filter(item => {
    const itemKey = key(item);
    if (seen.has(itemKey)) return false;
    seen.add(itemKey);
    return true;
  })];
};
const mergeWholeSurveyPage = (previous, page) => ({
  ...previous,
  ...page,
  mappings: page.mappings?.length ? page.mappings : previous.mappings,
  instruments: page.instruments?.length ? page.instruments : previous.instruments,
  coverage: page.coverage?.length ? page.coverage : previous.coverage,
  inherent_compatibility: page.inherent_compatibility?.length ? page.inherent_compatibility : previous.inherent_compatibility,
  overlaps: page.overlaps?.length ? page.overlaps : previous.overlaps,
  navigation_rows: appendServerOrdered(previous.navigation_rows, page.navigation_rows, row => row.cursor || row.variable_name),
  navigation_occurrences: appendServerOrdered(previous.navigation_occurrences, page.navigation_occurrences, item => `${item.instrument_id}/${item.question_id}`),
  navigation_pair_statuses: appendServerOrdered(previous.navigation_pair_statuses, page.navigation_pair_statuses, item => `${item.variable_name || ''}/${item.left_instrument_id}/${item.right_instrument_id}/${item.status}/${item.reason || ''}`),
  cells: appendServerOrdered(previous.cells, page.cells, cell => `${resultMappingKey(cell.mapping)}/${cell.question_id}`),
  pairwise_comparisons: appendServerOrdered(previous.pairwise_comparisons, page.pairwise_comparisons, item => `${resultMappingKey(item.left_mapping)}/${resultMappingKey(item.right_mapping)}/${item.variable_name}/${item.left_question_id}/${item.right_question_id}`),
  unattributed_answered_session_counts: appendServerOrdered(previous.unattributed_answered_session_counts, page.unattributed_answered_session_counts, item => `${item.deployment_id}/${item.question_id}`),
  compatibility_inventory: appendServerOrdered(previous.compatibility_inventory, page.compatibility_inventory, item => `${item.left_instrument_id}/${item.right_instrument_id}/${item.variable_name}/${item.status}/${item.reason || ''}`),
});

const comparisonIsPermitted = (comparison, result) => {
  // Treat pair evidence as an explicit server authorization.  A truthy or
  // merely non-blocked status is not enough: unknown wire values must never
  // make an effect or a statistical result appear.
  const status = comparison?.status;
  const mappingInstrument = (mapping) => (result?.mappings || []).find(item => resultMappingKey(item) === resultMappingKey(mapping))?.pinned_survey_version_id;
  const leftInstrument = mappingInstrument(comparison?.left_mapping);
  const rightInstrument = mappingInstrument(comparison?.right_mapping);
  if (!leftInstrument || !rightInstrument) return false;
  if (leftInstrument === rightInstrument) {
    // The query contract emits an exact "inherent" pair status for mappings
    // that share one pinned instrument.  Both that status and the returned
    // inherent record are required; no fallback compatibility is inferred.
    return status === 'inherent' && (result?.inherent_compatibility || []).some(item => item.instrument_id === leftInstrument && item.state === 'inherent');
  }
  if (status !== 'compatible') return false;
  const coverage = (result?.coverage || []).find(item => (item.left_instrument_id === leftInstrument && item.right_instrument_id === rightInstrument) || (item.left_instrument_id === rightInstrument && item.right_instrument_id === leftInstrument));
  // The service's wire tiers are snake case. "Partially comparable" remains
  // supported only as the exact pre-existing presentation fixture alias.
  return Boolean(coverage) && new Set(['comparable', 'partially_comparable', 'Comparable survey variants', 'Partially comparable']).has(coverage.tier);
};

// The service names its methods, effects, and refusals in wire codes. Printing
// them raw is labelling, not reporting: "INSUFFICIENT_CALCULATION_N" tells an
// author nothing they can act on, while "too few answers to calculate" tells
// them the question was fine and the data was thin.
const METHOD_LABEL = {
  mann_whitney_u: 'Mann–Whitney',
  welch_t_test: 'Welch t-test',
  chi_squared: 'Chi-squared',
  fisher_exact: 'Fisher exact',
};
const EFFECT_LABEL = {
  rank_biserial: 'rank-biserial',
  cohens_d: "Cohen's d",
  cramers_v: "Cramér's V",
  odds_ratio: 'odds ratio',
};
const WITHHELD_LABEL = {
  INSUFFICIENT_CALCULATION_N: 'too few answers to calculate',
  UNSUPPORTED_DOMAIN: 'this answer type cannot be compared statistically',
  OVERLAP_DETECTED: 'the same people appear in both Segments',
};
const STATUS_LABEL = {
  present_in_one_instrument: 'asked in only one of the two surveys',
  not_comparable: 'the two questions are not structurally comparable',
  ambiguous_duplicate: 'the variable name is used more than once',
};
const readable = (dictionary, code, fallback) => {
  if (!code) return fallback;
  if (dictionary[code]) return dictionary[code];
  // A wire code is one token of snake case. Anything containing a space is
  // already prose the service wrote for a human, and lowercasing it would
  // damage a sentence rather than translate a code.
  const raw = String(code);
  return /\s/.test(raw) ? raw : raw.toLowerCase().replace(/_/g, ' ');
};
// Why a pair produced no comparison. `status` alone is not the answer: a pair
// can be flagged "compatible" and still be blocked because its two surveys
// fell below the coverage tier, and reporting that as "no comparison:
// compatible" is worse than saying nothing.
const noComparisonReason = (pair) => {
  const known = STATUS_LABEL[pair.reason] || STATUS_LABEL[pair.status];
  if (known) return known;
  if (pair.status === 'compatible' || pair.status === 'inherent') return 'these two surveys are not similar enough to compare';
  return readable(STATUS_LABEL, pair.reason || pair.status, 'the service did not authorize this comparison');
};
// A p-value is read against a threshold, so it needs enough decimals to sit on
// one side of it; 1 and 0.05 must not both render as "1.0" or "0.1".
const pLabel = (value) => (typeof value !== 'number' ? displayValue(value) : (value < 0.001 ? '< 0.001' : value.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')));
const statLabel = (value) => (typeof value !== 'number' ? displayValue(value) : (Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')));

function OverlapSummary({ result, mappingByKey }) {
  const overlaps = (result.overlaps || []).filter(item => resultMappingKey(item.left_mapping) !== resultMappingKey(item.right_mapping));
  if (!overlaps.length) return null;
  // One direction per pair: A-vs-B and B-vs-A are the same question asked twice.
  const seen = new Set();
  const pairs = overlaps.filter((item) => {
    const key = [resultMappingKey(item.left_mapping), resultMappingKey(item.right_mapping)].sort().join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const sentence = (item) => {
    const known = Number(item.known_overlap_count || 0);
    const unknown = Number(item.identity_unknown_count || 0);
    if (item.overlap_status === 'detected') return `${numberLabel(known)} of the same ${known === 1 ? 'person appears' : 'people appear'} in both, so the two groups are not independent.`;
    if (item.overlap_status === 'none') return 'No one appears in both, so the two groups are independent.';
    return `No shared ${'\u2018'}people${'\u2019'} were found, but ${numberLabel(unknown)} ${unknown === 1 ? 'answer' : 'answers'} could not be traced to a person across deployments, so an overlap cannot be ruled out.`;
  };
  return <section aria-label="Whether the same people are in both Segments">
    <h4 style={{ margin: '0 0 4px' }}>Are the same people in both Segments?</h4>
    {pairs.map((item, index) => <div key={`${resultMappingKey(item.left_mapping)}-${resultMappingKey(item.right_mapping)}-${index}`} style={{ fontSize: 13 }}>
      <strong>{refDescription(item.left_mapping, mappingByKey)} and {refDescription(item.right_mapping, mappingByKey)}</strong>: {sentence(item)}
    </div>)}
  </section>;
}


// The nominal categories a set of cells reports, in first-seen order. Ordered
// scales are left out: they are steps of one measure, not separate identities,
// and they keep a single fill.
function nominalCategoryUnion(cells = []) {
  const union = [];
  const seen = new Set();
  cells.filter(Boolean).forEach((cell) => {
    const detail = asObject(cell.detail);
    const derived = asObject(detail.derived_categories);
    const counts = { ...asObject(detail.category_counts), ...asObject(derived.category_counts) };
    Object.keys(counts).forEach((category) => {
      if (seen.has(category)) return;
      seen.add(category);
      union.push(category);
    });
  });
  return union;
}

function MappingCell({ mapping, occurrence, cell, categoryColors = null }) {
  if (mapping.pinned_unavailable) return <article aria-label={`${mapping.logical_segment_name || 'Segment'} unavailable survey version`} style={{ border: '1px solid #DADCE0', borderRadius: 6, padding: 12 }}>Survey version unavailable.</article>;
  if (!occurrence) return <article aria-label={`${mapping.logical_segment_name || 'Segment'} absent question`} style={{ border: '1px solid #DADCE0', borderRadius: 6, padding: 12 }}>Question absent from this survey.</article>;
  const provenance = `${occurrence.authored_number_unavailable ? 'No authored number' : occurrence.authored_number || 'No authored number'} · ${occurrence.text || 'No question wording returned'} · ${occurrence.status || 'Returned status'}${occurrence.reason ? `: ${occurrence.reason}` : ''}`;
  if (!cell) return <article aria-label={`${mapping.logical_segment_name || 'Segment'} defined question`} style={{ border: '1px solid #DADCE0', borderRadius: 6, padding: 12 }}><div>Question occurrence: {provenance}</div><div>Defined question has no returned result cell.</div></article>;
  return <div><MappingResultCard mapping={mapping} cell={cell} canonicalType={occurrence.question_type} categoryColors={categoryColors} showRawAnswers={false} /></div>;
}

// One line: every question number the variants gave this question, then the
// question as written, once.
//
//   Q2 · Qactivity · Which activity helped you learn most? Lecture, ...
//
// Two variants routinely number the same question differently, so the numbers
// are listed in the order of the columns they belong to and the author can see
// which is which. They just as routinely word it at different lengths, and
// printing both wordings repeats the same sentence twice; the fullest one says
// everything the shorter one does.
function RowInventory({ row, result, mappings = [] }) {
  const occurrences = (result.navigation_occurrences || []).filter(item => sameVariable(item.variable_name, row.variable_name));
  const columnOf = (item) => {
    const index = mappings.findIndex(mapping => mapping.pinned_survey_version_id === item.instrument_id);
    return index < 0 ? mappings.length : index;
  };
  const ordered = [...occurrences].sort((left, right) => columnOf(left) - columnOf(right));
  const numbers = [];
  ordered.forEach((item) => {
    const number = item.authored_number_unavailable ? '' : (item.authored_number || '');
    if (number && !numbers.includes(number)) numbers.push(number);
  });
  // Variants usually word a question at different lengths — one prompt spells
  // the choices out, the other stops at the question mark. When one wording
  // contains the other they are the same question and the fuller one says
  // everything the shorter does, so it stands alone. Genuinely different
  // sentences are different questions sharing a variable name, and hiding one
  // of those would hide the ambiguity that makes the row worth reading.
  const texts = [];
  ordered.forEach((item) => {
    const text = (item.text || '').trim();
    if (!text) return;
    const overlapping = texts.findIndex(existing => existing.includes(text) || text.includes(existing));
    if (overlapping < 0) texts.push(text);
    else if (text.length > texts[overlapping].length) texts[overlapping] = text;
  });
  // The range a rating was collected on. "4" means nothing until the reader
  // knows whether the top of the scale is 5 or 10.
  const scale = ordered.find(item => item.scale_min !== undefined && item.scale_max !== undefined);
  return <div aria-label="Question">
    <div>{[...numbers, ...texts].filter(Boolean).join(' · ')}</div>
    {scale && <div style={{ color: '#5F6368', fontSize: 12, marginTop: 2 }}>Rating scale {scale.scale_min} to {scale.scale_max}</div>}
  </div>;
}

// What the comparison of these two Segments on this question actually found.
//
// The answer is a number or it is a reason there is no number — never a wire
// code. Every question here refuses for a different, actionable cause: a scale
// compares, a free-text question cannot, a question asked in only one variant
// has nothing to compare against, and a question everyone skipped has too
// little data. An author can act on each of those sentences; none of them can
// act on "UNSUPPORTED_DOMAIN".
// A number on a line beats a number alone: "U 200" says nothing until you can
// see that 200 sits exactly at the no-difference point of a 0-400 range. Only
// quantities with KNOWN bounds get a track — an unbounded statistic would need
// an invented axis, and that would be a picture of nothing.
function EvidenceTrack({ label, value, min, max, neutral, format = statLabel }) {
  if (typeof value !== 'number' || typeof min !== 'number' || typeof max !== 'number' || max <= min) return null;
  const clamp = (input) => Math.min(100, Math.max(0, ((input - min) / (max - min)) * 100));
  const position = clamp(value);
  const neutralAt = typeof neutral === 'number' ? clamp(neutral) : null;
  return <div style={{ display: 'grid', gridTemplateColumns: 'minmax(90px, auto) minmax(120px, 200px) auto', gap: 8, alignItems: 'center', justifyContent: 'end' }}>
    <span style={{ color: '#5F6368', textAlign: 'right' }}>{label}</span>
    <span role="img" aria-label={`${label} ${format(value)} on a scale from ${format(min)} to ${format(max)}`} style={{ position: 'relative', display: 'block', height: 14, background: '#E8EAED', borderRadius: 3 }}>
      {neutralAt !== null && <span data-track-neutral style={{ position: 'absolute', left: `${neutralAt}%`, top: -2, height: 18, borderLeft: '1px dashed #9AA0A6' }} />}
      <span data-track-marker={label} style={{ position: 'absolute', left: `${position}%`, top: -1, transform: 'translateX(-50%)', width: 10, height: 16, borderRadius: 2, background: SINGLE_BAR_COLOR, boxShadow: '0 0 0 2px #FFFFFF' }} />
    </span>
    <span style={{ color: '#202124', minWidth: 40, textAlign: 'right' }}>{format(value)}</span>
  </div>;
}

// The bounds a returned quantity is read against, where the maths fixes them.
const trackFor = (name, value, counts) => {
  const [left, right] = counts;
  const product = typeof left === 'number' && typeof right === 'number' ? left * right : null;
  switch (name) {
    // Rank-biserial and Cramer's V are correlation-like and bounded by
    // definition; U runs 0..n1*n2 and sits at half of that when the two groups
    // are indistinguishable.
    case 'rank_biserial': return { min: -1, max: 1, neutral: 0 };
    case 'cramers_v': return { min: 0, max: 1, neutral: 0 };
    case 'mann_whitney_u': return product ? { min: 0, max: product, neutral: product / 2 } : null;
    default: return null;
  }
};

function RowComparisons({ row, result, mappingByKey }) {
  const comparisons = (result.pairwise_comparisons || []).filter(item => sameVariable(item.variable_name, row.variable_name));
  if (!comparisons.length) return null;
  const countsFor = (pair, inputs = []) => [pair.left_mapping, pair.right_mapping]
    .map(ref => inputs.find(input => resultMappingKey(input.mapping) === resultMappingKey(ref))?.calculation_n)
    .filter(value => typeof value === 'number');
  return <div aria-label={`Comparison of ${row.variable_name || 'question'}`} style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, alignItems: 'flex-end', textAlign: 'right' }}>
    {comparisons.map((pair, index) => {
      const pairName = `${refDescription(pair.left_mapping, mappingByKey)} vs ${refDescription(pair.right_mapping, mappingByKey)}`;
      const permitted = comparisonIsPermitted(pair, result);
      // Anything short of a permitted, unwithheld pair states its reason and
      // stops. Reporting the reason IS the result in that case.
      if (!permitted || pair.withholding_reason) {
        const why = pair.withholding_reason
          ? readable(WITHHELD_LABEL, pair.withholding_reason, 'a prerequisite is unmet')
          : noComparisonReason(pair);
        return <div key={`${resultMappingKey(pair.left_mapping)}-${resultMappingKey(pair.right_mapping)}-${index}`}>
          <strong>{pairName}</strong>
          <span style={{ color: '#5F6368' }}> — no comparison: {why}</span>
        </div>;
      }
      const statistics = pair.statistics || [];
      const effects = pair.effects || [];
      const counts = countsFor(pair, statistics[0]?.group_inputs || effects[0]?.group_inputs);
      const parts = [];
      if (pair.difference !== undefined && pair.difference !== null) parts.push(`difference ${typeof pair.difference === 'object' ? displayValue(pair.difference) : statLabel(pair.difference)}`);
      statistics.forEach((statistic) => {
        const method = readable(METHOD_LABEL, statistic.method, 'Returned method');
        const suffix = statistic.statistic_name && !method.endsWith(statistic.statistic_name) ? ` ${statistic.statistic_name}` : '';
        const adjusted = typeof statistic.adjusted_p_value === 'number' ? `, adjusted p ${pLabel(statistic.adjusted_p_value)}` : '';
        parts.push(`${method}${suffix} ${statLabel(statistic.statistic)}, p ${pLabel(statistic.p_value)}${adjusted}`);
      });
      effects.forEach((effect) => { parts.push(`${readable(EFFECT_LABEL, effect.name, 'effect')} ${statLabel(effect.value)}`); });
      if (counts.length === 2) parts.push(`n ${numberLabel(counts[0])} vs ${numberLabel(counts[1])}`);
      // Every bounded quantity gets its range drawn. A marker sitting on the
      // dashed no-difference line says "these two groups look alike" faster
      // than any sentence can.
      const tracks = [
        ...statistics.map((statistic) => ({ name: statistic.method, label: `${readable(METHOD_LABEL, statistic.method, 'statistic')}${statistic.statistic_name && !readable(METHOD_LABEL, statistic.method, '').endsWith(statistic.statistic_name) ? ` ${statistic.statistic_name}` : ''}`, value: statistic.statistic })),
        ...effects.map(effect => ({ name: effect.name, label: readable(EFFECT_LABEL, effect.name, 'effect'), value: effect.value })),
      ].map(item => ({ ...item, bounds: trackFor(item.name, item.value, counts) })).filter(item => item.bounds);
      const pValues = statistics.map(statistic => statistic.p_value).filter(value => typeof value === 'number');
      const notes = [...new Set([
        ...statistics.flatMap(statistic => statistic.assumptions || []),
        ...statistics.map(statistic => statistic.observational_interpretation).filter(Boolean),
        ...(pair.qualifications || []).map(item => item.message).filter(Boolean),
      ])];
      return <div key={`${resultMappingKey(pair.left_mapping)}-${resultMappingKey(pair.right_mapping)}-${index}`} style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
        <div><strong>{pairName}</strong>{parts.length ? <span> — {parts.join(' · ')}</span> : <span style={{ color: '#5F6368' }}> — comparable; no statistic was returned</span>}</div>
        {tracks.map(track => <EvidenceTrack key={track.label} label={track.label} value={track.value} min={track.bounds.min} max={track.bounds.max} neutral={track.bounds.neutral} />)}
        {/* p is drawn against 0.05 rather than 0, because 0.05 is the line a
            reader is actually measuring it against. */}
        {pValues.map((value, pIndex) => <EvidenceTrack key={`p-${pIndex}`} label="p (0.05 marked)" value={value} min={0} max={1} neutral={0.05} format={pLabel} />)}
        {notes.map(note => <div key={note} style={{ color: '#5F6368' }}>{note}</div>)}
      </div>;
    })}
  </div>;
}

// Answered sessions that could not be attributed to a recipient. A zero is the
// normal case and saying it on every row of every question buries the one that
// is not zero, so only a real count is reported.
function RowDeploymentFacts({ row, result }) {
  const questionIDs = new Set((result.navigation_occurrences || []).filter(item => sameVariable(item.variable_name, row.variable_name)).map(item => item.question_id));
  const facts = new Map();
  (result.unattributed_answered_session_counts || [])
    .filter(item => questionIDs.has(item.question_id) && Number(item.unattributed_answered_session_count) > 0)
    .forEach((item) => {
      const key = `${item.deployment_id}/${item.question_id}`;
      if (!facts.has(key)) facts.set(key, item);
    });
  if (!facts.size) return null;
  const segmentNames = new Map();
  (result.mappings || []).forEach((mapping) => {
    const deployment = mapping.mapping?.deployment_id || mapping.deployment_id;
    if (deployment && !segmentNames.has(deployment)) segmentNames.set(deployment, mapping.logical_segment_name || mapping.survey_label || deployment);
  });
  return <div aria-label={`Unattributed answers for ${row.variable_name || 'question'}`} style={{ fontSize: 13, color: '#5F6368' }}>
    {[...facts.values()].map(item => <div key={`${item.deployment_id}/${item.question_id}`}>
      {numberLabel(item.unattributed_answered_session_count)} answered {Number(item.unattributed_answered_session_count) === 1 ? 'session' : 'sessions'} in {segmentNames.get(item.deployment_id) || item.deployment_id} could not be matched to a recipient
    </div>)}
  </div>;
}

function WholeSurveyReport({ result, onLoadNextPage, loadingNextPage, abFocus }) {
  const reportRef = useRef(null);
  const [columnStart, setColumnStart] = useState(0);
  const mappings = useMemo(() => Array.isArray(result?.mappings) ? result.mappings : [], [result]);
  const rows = useMemo(() => Array.isArray(result?.navigation_rows) ? result.navigation_rows : [], [result]);
  const mappingByKey = useMemo(() => new Map(mappings.map(mapping => [resultMappingKey(mapping), mapping])), [mappings]);
  const virtualized = mappings.length >= 5;
  const visibleMappings = virtualized ? mappings.slice(columnStart, columnStart + 4) : mappings;

  // The report runs to the bottom of the window. A fixed height left dead space
  // under a long table on a tall screen and clipped it on a short one, so the
  // height is measured from where the report actually starts.
  const [reportHeight, setReportHeight] = useState(null);
  useEffect(() => {
    const fit = () => {
      const top = reportRef.current?.getBoundingClientRect().top;
      if (typeof top !== 'number') return;
      const next = Math.max(MIN_REPORT_HEIGHT, Math.round(window.innerHeight - top - REPORT_BOTTOM_GUTTER));
      // Only a real move is worth a render. Writing the same number back would
      // resize the element, which re-measures, which writes again.
      setReportHeight(current => (current !== null && Math.abs(current - next) <= 1 ? current : next));
    };
    fit();
    window.addEventListener('resize', fit);
    // The report is measured from where it sits in the VIEWPORT, and the page
    // it sits on scrolls. Measuring once on mount pinned the height to whatever
    // the report's offset happened to be while it was still below the fold —
    // which is how a full-height report ended up clamped to its floor. Capture
    // catches ancestor scrollers too, since the panel is not the scroll root.
    window.addEventListener('scroll', fit, true);
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
    if (observer && document.body) observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('scroll', fit, true);
      observer?.disconnect();
    };
  }, [rows.length, mappings.length]);

  useEffect(() => {
    const target = abFocus?.key === 'all'
      ? reportRef.current
      : reportRef.current?.querySelector(`[data-analysis-row-key="${String(abFocus?.key || '').replace(/"/g, '\\"')}"]`);
    target?.scrollIntoView?.({ block: 'nearest' });
    // Keep keyboard focus on the labelled report container while the target
    // row is scrolled into view. This preserves a stable focus landmark for
    // screen readers and exposes the focused row through aria-activedescendant.
    reportRef.current?.focus?.();
  }, [abFocus, rows]);

  useEffect(() => { setColumnStart(start => Math.min(start, Math.max(0, mappings.length - 4))); }, [mappings.length]);

  const occurrencesFor = (row, mapping) => {
    const instrumentID = mapping.pinned_survey_version_id;
    return (result.navigation_occurrences || []).filter(item => sameVariable(item.variable_name, row.variable_name) && (!instrumentID || item.instrument_id === instrumentID));
  };
  const cellFor = (mapping, occurrence) => (result.cells || []).find(cell => resultMappingKey(cell.mapping) === resultMappingKey(mapping) && occurrence?.question_id === cell.question_id);
  // One colour per category across a whole row, so a category reads the same in
  // every mapping column. Built from ALL mappings rather than the visible four,
  // so paging the column window never repaints the columns that stay on screen.
  const rowCategoryColors = (row) => categoryColorMap(nominalCategoryUnion(
    mappings.flatMap(mapping => occurrencesFor(row, mapping).map(occurrence => cellFor(mapping, occurrence)))));

  const focusedRowIndex = rows.findIndex((row, index) => wholeRowKey(row, index) === abFocus?.key);
  return <section id="analysis-whole-survey-report" ref={reportRef} tabIndex="-1" aria-label="A/B analysis report" aria-activedescendant={focusedRowIndex >= 0 ? wholeRowID(rows[focusedRowIndex], focusedRowIndex) : undefined} style={{ maxHeight: reportHeight ?? MIN_REPORT_HEIGHT, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
    <h3>Population Analysis</h3>
    <div aria-live="polite">{abFocus?.label || 'All questions'} navigation focus</div>
    <OverlapSummary result={result} mappingByKey={mappingByKey} />
    {virtualized && <div aria-label="Mapping column window" style={{ display: 'flex', gap: 8, alignItems: 'center' }}><button type="button" onClick={() => setColumnStart(start => Math.max(0, start - 1))} disabled={columnStart === 0}>Previous mapping columns</button><span>Mappings {columnStart + 1}–{Math.min(columnStart + 4, mappings.length)} of {mappings.length}</span><button type="button" onClick={() => setColumnStart(start => Math.min(mappings.length - 4, start + 1))} disabled={columnStart + 4 >= mappings.length}>Next mapping columns</button></div>}
    {!rows.length ? <div role="status">No report rows were returned.</div> : <div style={{ overflowX: 'auto' }}><table aria-label="Whole-survey A/B report" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '100%' }}><thead><tr><th scope="col" style={{ position: 'sticky', left: 0, zIndex: 2, background: QUESTION_COLUMN_BG, minWidth: 300, textAlign: 'left', verticalAlign: 'top', padding: 10, borderBottom: '1px solid #DADCE0', top: 0 }}>Question</th>{visibleMappings.map(mapping => <th key={resultMappingKey(mapping)} scope="col" style={{ minWidth: 310, textAlign: 'left', verticalAlign: 'top', padding: 10, borderBottom: '1px solid #DADCE0', position: 'sticky', top: 0, zIndex: 1, background: '#FFF' }}><MappingLabel candidate={mapping} /></th>)}</tr></thead><tbody>{rows.map((row, rowIndex) => <React.Fragment key={wholeRowKey(row, rowIndex)}><tr id={wholeRowID(row, rowIndex)} data-analysis-row-key={wholeRowKey(row, rowIndex)} tabIndex="-1" style={{ background: rowIndex % 2 ? BAND_BG : 'transparent' }}><th scope="row" style={{ position: 'sticky', left: 0, zIndex: 1, background: rowIndex % 2 ? QUESTION_COLUMN_BAND_BG : QUESTION_COLUMN_BG, textAlign: 'left', verticalAlign: 'top', padding: 10, borderBottom: 'none' }}><RowInventory row={row} result={result} mappings={mappings} /></th>{(() => { const rowColors = rowCategoryColors(row); return visibleMappings.map(mapping => { const occurrences = occurrencesFor(row, mapping); return <td key={resultMappingKey(mapping)} style={{ verticalAlign: 'top', padding: 10, borderBottom: '1px solid #E8EAED' }}>{occurrences.length ? occurrences.map(occurrence => <MappingCell key={occurrence.question_id} mapping={mapping} occurrence={occurrence} cell={cellFor(mapping, occurrence)} categoryColors={rowColors} />) : <MappingCell mapping={mapping} occurrence={null} cell={null} />}</td>; }); })()}</tr><tr style={{ background: rowIndex % 2 ? BAND_BG : 'transparent' }}><td style={{ position: 'sticky', left: 0, zIndex: 1, background: rowIndex % 2 ? QUESTION_COLUMN_BAND_BG : QUESTION_COLUMN_BG, borderBottom: '1px solid #DADCE0' }} /><td colSpan={visibleMappings.length} style={{ padding: '0 10px 14px', borderBottom: '1px solid #DADCE0' }}><RowComparisons row={row} result={result} mappingByKey={mappingByKey} /><RowDeploymentFacts row={row} result={result} /></td></tr></React.Fragment>)}</tbody></table></div>}
    {result.next_row_cursor && <button type="button" onClick={onLoadNextPage} disabled={loadingNextPage}>{loadingNextPage ? 'Loading more report rows…' : 'Load more report rows'}</button>}
  </section>;
}

export default function AnalysisComparePanel({ deploymentId, selectedQuestion, selectedMappings, onSelectedMappingsChange, onWholeSurveyChange, onNavigationRows, abFocus }) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [searchState, setSearchState] = useState({ loading: false, error: '', candidates: [], paging: { page: 1, page_size: PAGE_SIZE, total_count: 0 } });
  const [comparisonState, setComparisonState] = useState({ loading: false, error: '', result: null });
  const [exportError, setExportError] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);
  // Counts answer "how many"; shares answer "how does the mix differ" — the
  // question an author actually asks when Segments are different sizes.
  const [showProportion, setShowProportion] = useState(false);
  const [showCumulative, setShowCumulative] = useState(true);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const searchRef = useRef(null);
  const requestRef = useRef(0);
  const requestedWholeCursors = useRef(new Set());
  const selectedMappingRefs = useMemo(() => selectedMappings.map(mappingRef), [selectedMappings]);

  // The server includes currently selected refs with all searches. Keep our
  // local objects as the durable display fallback so paging/search cannot make
  // a selection disappear even if it is not on the returned page.
  useEffect(() => {
    if (!deploymentId) return undefined;
    let active = true;
    setSearchState(previous => ({ ...previous, loading: true, error: '' }));
    searchAnalysisSegmentMappings({
      current_deployment_id: deploymentId,
      query,
      page,
      page_size: PAGE_SIZE,
      include_mappings: selectedMappingRefs,
    }).then((result) => {
      if (!active) return;
      const candidates = Array.isArray(result?.candidates) ? result.candidates : [];
      setSearchState({ loading: false, error: '', candidates, paging: result?.paging || { page, page_size: PAGE_SIZE, total_count: candidates.length } });
    }).catch((error) => {
      if (active) setSearchState(previous => ({ ...previous, loading: false, error: error.message || 'Could not load Segment mappings.' }));
    });
    return () => { active = false; };
  }, [deploymentId, page, query, selectedMappingRefs, onSelectedMappingsChange]);

  const allInOpenDeployment = selectedMappings.length >= 2 && selectedMappings.every(item => mappingRef(item).deployment_id === deploymentId);
  const wholeSurvey = selectedMappings.length >= 2 && !allInOpenDeployment;
  useEffect(() => { onWholeSurveyChange(wholeSurvey); }, [wholeSurvey, onWholeSurveyChange]);
  // The question in the Analysis bar is the only question context; Compare
  // reads it directly rather than resolving it against a second list.
  const canonicalQuestion = selectedQuestion || null;

  useEffect(() => {
    const sequence = ++requestRef.current;
    if (selectedMappings.length < 2 || (allInOpenDeployment && !canonicalQuestion)) {
      onNavigationRows([]);
      setComparisonState({ loading: false, error: '', result: null });
      return undefined;
    }
    const body = { mappings: selectedMappings.map(mappingRef), row_page_size: 50 };
    if (allInOpenDeployment) body.question_id = canonicalQuestion.question_id;
    requestedWholeCursors.current = new Set();
    setComparisonState({ loading: true, error: '', result: null });
    queryAnalysisSegmentComparisons(body).then((result) => {
      if (sequence === requestRef.current) {
        setComparisonState({ loading: false, error: '', result });
        onNavigationRows(Array.isArray(result?.navigation_rows) ? result.navigation_rows : []);
      }
    }).catch((error) => {
      if (sequence === requestRef.current) {
        onNavigationRows([]);
        setComparisonState({ loading: false, error: error.message || 'Could not load comparison.', result: null });
      }
    });
    return undefined;
  }, [selectedMappings, allInOpenDeployment, canonicalQuestion, onNavigationRows]);

  const loadNextWholeSurveyPage = useCallback(() => {
    const current = comparisonState.result;
    const cursor = current?.next_row_cursor;
    if (!cursor || comparisonState.loading || current.kind !== 'whole_survey' || requestedWholeCursors.current.has(cursor)) return;
    requestedWholeCursors.current.add(cursor);
    const sequence = ++requestRef.current;
    setComparisonState(previous => ({ ...previous, loading: true, error: '' }));
    queryAnalysisSegmentComparisons({ mappings: selectedMappingRefs, row_page_size: 50, row_cursor: cursor }).then((pageResult) => {
      if (sequence !== requestRef.current || pageResult?.kind !== 'whole_survey') return;
      setComparisonState(previous => {
        const result = mergeWholeSurveyPage(previous.result || current, pageResult);
        onNavigationRows(result.navigation_rows || []);
        return { loading: false, error: '', result };
      });
    }).catch((error) => {
      if (sequence === requestRef.current) {
        requestedWholeCursors.current.delete(cursor);
        setComparisonState(previous => ({ ...previous, loading: false, error: error.message || 'Could not load more report rows.' }));
      }
    });
  }, [comparisonState.loading, comparisonState.result, onNavigationRows, selectedMappingRefs]);

  useEffect(() => {
    if (comparisonState.result?.kind === 'whole_survey' && comparisonState.result.next_row_cursor && !comparisonState.loading && !comparisonState.error) loadNextWholeSurveyPage();
  }, [comparisonState.error, comparisonState.loading, comparisonState.result, loadNextWholeSurveyPage]);

  const candidatesByKey = new Map(searchState.candidates.map(candidate => [candidateKey(candidate), candidate]));
  const visibleCandidates = [...candidatesByKey.values()];
  const selectedKeys = new Set(selectedMappings.map(candidateKey));

  // Declared intent (Section 11.1.1). Compare does exactly two things, and the
  // FIRST selection says which one the author is asking for: a whole-roster
  // mapping means a full-deployment A/B, a subset means groups within a roster.
  // The two are never mixed, so the chooser disables what the intent excludes
  // rather than letting the author build a selection Compare cannot answer.
  //
  // This is guidance about what to offer, not a second place where
  // comparability is decided — the tiers in Section 4.2 still govern what a
  // selected pair may report, and the backend still validates the shape.
  const intent = selectedMappings.length ? scopeOf(selectedMappings[0]) : '';
  const intentDeployment = intent === SUBSET ? mappingRef(selectedMappings[0]).deployment_id : '';
  const disabledReasonFor = (candidate) => {
    if (!intent) return '';
    // A selected mapping is never disabled: deselecting is how intent is
    // withdrawn, so the way back must always stay open.
    if (selectedKeys.has(candidateKey(candidate))) return '';
    if (intent === WHOLE_ROSTER) {
      return scopeOf(candidate) === WHOLE_ROSTER ? '' : 'Part of a roster cannot join a full-deployment A/B comparison.';
    }
    if (scopeOf(candidate) === WHOLE_ROSTER) return 'A whole roster cannot join a comparison of groups within a roster.';
    // "Groups within a roster" is ONE roster.
    return mappingRef(candidate).deployment_id === intentDeployment ? '' : 'Groups are compared within one roster.';
  };

  const toggleMapping = (candidate) => {
    if (disabledReasonFor(candidate)) return;
    const key = candidateKey(candidate);
    onSelectedMappingsChange(previous => previous.some(item => candidateKey(item) === key)
      ? previous.filter(item => candidateKey(item) !== key)
      : [...previous, candidate]);
  };
  const totalPages = Math.max(1, Math.ceil((searchState.paging?.total_count || 0) / PAGE_SIZE));
  const chooserStatus = selectedMappings.length === 0
    ? 'Select at least two Segment mappings.'
    : selectedMappings.length === 1
      ? 'Select at least one more Segment mapping.'
      : allInOpenDeployment && !canonicalQuestion
        ? 'Choose a survey question in the bar above to compare these Segments. Survey health views cannot be compared.'
        : comparisonState.loading
          ? 'Loading comparison…'
          : comparisonState.error || '';

  // Built once here so the chart, the summary box, and the Segment table all
  // read the same series — including the order the colours are assigned in.
  const intraSeries = useMemo(() => {
    const result = comparisonState.result;
    if (result?.kind !== 'intra_deployment') return null;
    return buildCompareSeries({
      mappings: Array.isArray(result.mappings) ? result.mappings : [],
      cells: Array.isArray(result.cells) ? result.cells : [],
      question: canonicalQuestion,
    });
  }, [comparisonState.result, canonicalQuestion]);

  const resultForExport = comparisonState.result;
  const exportCsv = () => {
    if (!resultForExport) return;
    setExportError('');
    try { downloadCompareCsv({ ...resultForExport, canonical_question: canonicalQuestion }); } catch (error) { setExportError(error.message || 'Could not export Compare data.'); }
  };
  const exportPdf = async () => {
    if (!resultForExport) return;
    setExportError(''); setExportingPdf(true);
    try { await downloadComparePdf({ ...resultForExport, canonical_question: canonicalQuestion }); } catch (error) { setExportError(error.message || 'Could not export Compare PDF.'); } finally { setExportingPdf(false); }
  };

  // The colour that identifies a Segment comes from its position in the drawn
  // series, so its swatch in the table, its bars, its line, and its summary row
  // are the same colour by construction.
  const colorIndexOf = (key) => (intraSeries?.segments || []).findIndex(segment => segment.key === key);
  const typeLabel = QUESTION_TYPE_LABEL[canonicalQuestion?.question_type] || canonicalQuestion?.question_type || 'Unavailable';
  const ready = comparisonState.result?.kind === 'intra_deployment';

  // One scroller for the whole surface. Neither column clips or scrolls on its
  // own: the panel grows to its content and the page scrolls, so nothing ends
  // up trapped inside a pane the author has to find.
  return <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Same toolbar shape as Describe: display choices on the right, an
          Export menu at the end. */}
      {resultForExport && <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }} />
        {ready && <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5F6368', cursor: 'pointer' }}>
            <input type="checkbox" checked={showProportion} onChange={() => setShowProportion(value => !value)} style={{ cursor: 'pointer' }} />
            Compare as share of each Segment
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5F6368', cursor: 'pointer' }}>
            <input type="checkbox" checked={showCumulative} onChange={() => setShowCumulative(value => !value)} style={{ cursor: 'pointer' }} />
            Cumulative line per Segment
          </label>
        </>}
        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => setExportMenuOpen(open => !open)} style={compareTopButtonStyle}>Export ▾</button>
          {exportMenuOpen && <>
            <div onClick={() => setExportMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 50, background: '#FFFFFF', border: '1px solid #DADCE0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 210, padding: '4px 0' }}>
              <button type="button" style={compareExportMenuItemStyle} onClick={() => { setExportMenuOpen(false); exportCsv(); }}>Data table (CSV)</button>
              <button type="button" style={compareExportMenuItemStyle} disabled={exportingPdf} onClick={() => { setExportMenuOpen(false); exportPdf(); }}>{exportingPdf ? 'Building PDF…' : 'Chart + table (PDF)'}</button>
            </div>
          </>}
        </div>
        {exportError && <span role="alert" style={{ fontSize: 12 }}>{exportError}</span>}
      </div>}
      {chooserStatus && <div aria-live="polite" role="status" style={{ color: '#5F6368', fontSize: 13 }}>{chooserStatus}</div>}
      {comparisonState.error && <div role="alert">{comparisonState.error}</div>}
      {ready && <IntraDeploymentResult
        result={comparisonState.result}
        canonicalQuestion={canonicalQuestion}
        series={intraSeries}
        showProportion={showProportion}
        showCumulative={showCumulative}
      />}
      {comparisonState.result?.kind === 'whole_survey' && <WholeSurveyReport result={comparisonState.result} onLoadNextPage={loadNextWholeSurveyPage} loadingNextPage={comparisonState.loading} abFocus={abFocus} />}
    </div>

    {/* Right column: the same summary box Describe shows, then the Segments. */}
    <div style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <CompareSummaryBox
        series={intraSeries}
        typeLabel={typeLabel}
        cells={comparisonState.result?.cells || []}
        mappings={selectedMappings}
      />
      <CompareSegmentPicker
        ref={searchRef}
        candidates={visibleCandidates}
        selectedKeys={selectedKeys}
        keyOf={candidateKey}
        onToggle={toggleMapping}
        disabledReasonOf={disabledReasonFor}
        onClearSelection={() => onSelectedMappingsChange([])}
        onClearSearch={() => { setQuery(''); setPage(1); searchRef.current?.focus(); }}
        query={query}
        onQueryChange={(value) => { setQuery(value); setPage(1); }}
        loading={searchState.loading}
        error={searchState.error}
        page={page}
        pageCount={totalPages}
        onPageChange={setPage}
        colorIndexOf={colorIndexOf}
      />
      {selectedMappings.length > 0 && <section aria-label="Selected Segment mappings" style={{ fontSize: 12 }}>
        <h3 style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', color: '#5F6368', margin: '0 0 4px' }}>Selected mappings</h3>
        {selectedMappings.map(candidate => <div key={candidateKey(candidate)} style={{ padding: '3px 0' }}><MappingLabel candidate={candidate} /></div>)}
      </section>}
      {/* Mappings from other deployments are ordinary rows in the one list
          above (Section 11.1), not a second collapsed list underneath it. */}
    </div>
  </div>;
}
